import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { func, string } from "prop-types";
import styled from "styled-components/macro";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import isEqual from "lodash/isEqual";

import { actions as compareActions } from "../compare/Compare.redux";
import { actions as searchActions } from "../search/Search.redux";
import { actions as watchActions } from "./Watch.redux";
import { useSearchContext } from "../search/Search.context";
import { formatNumber } from "../../helpers/format";

import Meta from "../meta";

const WatchDesktop = lazy(() => import("./components/Watch.desktop"));
const WatchMobile = lazy(() => import("./components/Watch.mobile"));

function Watch({ analyticsLabel = "watch", trackEvent, trackModalView }) {
  const activeQuery = useSearchContext(null, "watched");
  const { breadcrumbsUser, hasQuery, queryFull } = activeQuery;

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState();
  const [priceLow, setPriceLow] = useState(null);
  const [results, setResults] = useState([]);
  const [title, setTitle] = useState(defaultTitle);
  const [total, setTotal] = useState(0);

  const container = useRef();
  const isMounted = useRef(true);

  const authenticated = useSelector(state => !!state?.login.auth.expires);
  const comparedListings = useSelector(state => state?.comparedListings || []);
  const searchError = useSelector(state => state?.watchedListingsError);
  const searchLoading = useSelector(state => state?.watchedListingsLoading);
  const targetDevice = useSelector(state => state.targetDevice);

  function addToComparedListings(listing) {
    dispatch(compareActions.add(listing));
  }

  const clearSearch = useCallback(() => {
    dispatch(searchActions.resetSearchCurrentMeta());
    dispatch(watchActions.handleError(null));
  }, [dispatch]);

  function removeFromComparedListings(equipmentId) {
    dispatch(compareActions.remove(equipmentId));
  }

  // Get UI for target device
  const WatchComponent = useMemo(() => {
    return targetDevice === "desktop" ? WatchDesktop : WatchMobile;
  }, [targetDevice]);

  // Test if fetching results would be ok
  const activeQueryFull = useRef();
  const [canFetch, setCanFetch] = useState(false);
  useEffect(() => {
    if (queryFull !== activeQueryFull.current) {
      setCanFetch(true);
      activeQueryFull.current = queryFull;
    } else if (searchLoading) {
      setCanFetch(false);
    } else if (!!searchError) {
      setCanFetch(false);
      activeQueryFull.current = undefined;
    }
  }, [queryFull, searchError, searchLoading]);

  // Determine if search error should be displayed
  const [showError, setShowError] = useState(!!searchError);

  useEffect(() => {
    setShowError(!!searchError);
  }, [searchError]);

  // Fetch search results
  const cachedResults = useSelector(
    state => selectCachedResults(state, authenticated, queryFull),
    isEqual
  );

  const fetchSearchResults = useCallback(() => {
    if (queryFull !== undefined) {
      dispatch(watchActions.fetch(queryFull));
    }
  }, [dispatch, queryFull]);

  useEffect(() => {
    if (!authenticated) {
      return false;
    }

    if (isMounted.current && !cachedResults && canFetch) {
      setLoading(true);
      fetchSearchResults();
    }
  }, [authenticated, cachedResults, canFetch, fetchSearchResults]);

  useEffect(() => {
    if (!authenticated) {
      return false;
    }

    if (isMounted.current && !!cachedResults) {
      const { meta = {}, results = [] } = cachedResults;
      setResults(results);
      setPagination(meta.pagination);
      setPriceLow(
        !!meta?.price?.defaultValues[0] &&
          `$${formatNumber(meta.price.defaultValues[0])}`
      );
      setTotal(meta.total || 0);
      setLoading(false);
      dispatch(searchActions.handleSearchCurrentMeta(meta));
      setShowError(false);
    }
  }, [authenticated, cachedResults, dispatch]);

  // Zero out results when API returns an error
  useEffect(() => {
    if (!!searchError) {
      setResults([]);
      setPagination(undefined);
      setTotal(0);
      setLoading(false);
    }
  }, [searchError]);

  // Update page title
  useEffect(() => {
    const preposition = hasQuery ? "" : "with ";
    const nextTitle =
      breadcrumbsUser.length > 0
        ? `${defaultTitle} ${preposition} ${breadcrumbsUser
            .map(({ key, value }) => value)
            .join("; ")}`
        : defaultTitle;
    setTitle(nextTitle);
  }, [breadcrumbsUser, hasQuery]);

  // Cleanup when unmounted
  useEffect(() => {
    return () => {
      if (!!clearSearch) {
        clearSearch();
      }
    };
  }, [clearSearch]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <Container ref={container}>
      <Meta title={title} />
      <Suspense fallback={null}>
        <WatchComponent
          {...activeQuery}
          addToComparedListings={addToComparedListings}
          analyticsLabel={analyticsLabel}
          authenticated={authenticated}
          comparedListings={comparedListings}
          error={searchError}
          loading={loading}
          pagination={pagination}
          priceLow={priceLow}
          removeFromComparedListings={removeFromComparedListings}
          results={results}
          showError={showError}
          targetDevice={targetDevice}
          total={total}
          trackEvent={trackEvent}
          trackModalView={trackModalView}
        />
      </Suspense>
    </Container>
  );
}

const defaultTitle = "My Watched Listings";

const Container = styled.div`
  height: 100%;
`;

const selectCachedResults = createSelector(
  [
    state => state?.watchedListings,
    (state, authenticated) => authenticated,
    (state, authenticated, queryFull) => queryFull,
  ],
  (watchedListings, authenticated, queryFull) => {
    const cachedResults =
      !!watchedListings &&
      !Array.isArray(watchedListings) && // shape changed from array to object
      queryFull !== undefined &&
      watchedListings.query === queryFull
        ? watchedListings
        : null;

    if (!cachedResults) {
      return null;
    } else {
      return cachedResults;
    }
  }
);

Watch.propTypes = {
  analyticsLabel: string,
  trackEvent: func.isRequired,
  trackModalView: func.isRequired,
};

export default Watch;
